// Wholesale order line items table.
// Supports inline qty/price editing and per-line serial scan for serial-tracked items.
// Serials are fetched from the order's warehouse (status=in_stock + warehouse_id)
// and presented via SerialScanInput. Saving calls updateItem({itemId, serialIds}).

import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Trash2, ScanLine, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiGet } from "@/lib/api/client";
import { SerialScanInput } from "@/features/inventory/serial-scan-input";
import type { OrderItem } from "@/features/pos/use-pos-cart";

interface Serial {
  id: string;
  serialNo: string;
  status: string;
  currentWarehouseId?: string | null;
}

interface Props {
  items: OrderItem[];
  warehouseId: string | null;
  loading: boolean;
  /** Được phép sửa đơn giá / chiết khấu (cờ phân quyền). Khi false: chỉ hiển thị. */
  canEditPrice?: boolean;
  onUpdateQty: (itemId: string, qty: number) => void;
  onUpdatePrice: (itemId: string, price: string) => void;
  onUpdateDiscount: (itemId: string, discount: string) => void;
  onUpdateSerials: (itemId: string, serialIds: string[]) => void;
  onRemove: (itemId: string) => void;
}

function fmtVND(s: string) {
  const n = parseFloat(s);
  if (isNaN(n)) return "0";
  return new Intl.NumberFormat("vi-VN").format(n);
}

// Row that handles inline editing and serial assignment for a single line item.
function LineRow({
  item,
  warehouseId,
  loading,
  canEditPrice,
  onUpdateQty,
  onUpdatePrice,
  onUpdateDiscount,
  onUpdateSerials,
  onRemove,
}: {
  item: OrderItem;
  warehouseId: string | null;
  loading: boolean;
  canEditPrice?: boolean;
  onUpdateQty: (qty: number) => void;
  onUpdatePrice: (price: string) => void;
  onUpdateDiscount: (discount: string) => void;
  onUpdateSerials: (serialIds: string[]) => void;
  onRemove: () => void;
}) {
  const [qtyDraft, setQtyDraft] = useState(String(item.quantity));
  const [priceDraft, setPriceDraft] = useState(item.unitPrice);
  const [discountDraft, setDiscountDraft] = useState(item.discount);
  const [serialOpen, setSerialOpen] = useState(false);
  const [serialSelection, setSerialSelection] = useState<string[]>(
    () => (item.serials ?? []).map((s) => s.serialNo),
  );

  // Drafts are intentionally not reset on server refresh while the user is editing.
  // The onBlur handlers commit or revert each field individually.

  // Fetch sellable serials from the order's warehouse for this variant.
  // Only enabled when the serial panel is open and warehouse is known.
  const { data: availableSerials = [] } = useQuery({
    queryKey: ["serials", "sellable", item.variantId, warehouseId],
    queryFn: () =>
      apiGet<Serial[]>("/v1/serials", {
        variant_id: item.variantId,
        status: "in_stock",
        warehouse_id: warehouseId,
        limit: 200,
      }),
    enabled: serialOpen && !!warehouseId,
    staleTime: 15_000,
  });

  const handleQtyBlur = useCallback(() => {
    const n = parseInt(qtyDraft, 10);
    if (!isNaN(n) && n > 0 && n !== item.quantity) {
      onUpdateQty(n);
    } else {
      setQtyDraft(String(item.quantity));
    }
  }, [qtyDraft, item.quantity, onUpdateQty]);

  const handleQtyKey = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
    if (e.key === "Escape") setQtyDraft(String(item.quantity));
  }, [item.quantity]);

  const handlePriceBlur = useCallback(() => {
    const n = parseFloat(priceDraft);
    if (!isNaN(n) && n >= 0 && priceDraft !== item.unitPrice) {
      onUpdatePrice(priceDraft);
    } else {
      setPriceDraft(item.unitPrice);
    }
  }, [priceDraft, item.unitPrice, onUpdatePrice]);

  const handlePriceKey = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
    if (e.key === "Escape") setPriceDraft(item.unitPrice);
  }, [item.unitPrice]);

  const handleDiscountBlur = useCallback(() => {
    const n = parseFloat(discountDraft);
    if (!isNaN(n) && n >= 0 && discountDraft !== item.discount) {
      onUpdateDiscount(discountDraft);
    } else if (discountDraft.trim() === "" && item.discount !== "0") {
      onUpdateDiscount("0");
    } else {
      setDiscountDraft(item.discount);
    }
  }, [discountDraft, item.discount, onUpdateDiscount]);

  const handleDiscountKey = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
    if (e.key === "Escape") setDiscountDraft(item.discount);
  }, [item.discount]);

  // Map serial numbers to IDs using the fetched list.
  // The SerialScanInput works with serial *numbers* (user-visible strings),
  // but updateItem expects serial *IDs*. We resolve the mapping here.
  const serialNoToId = Object.fromEntries(
    availableSerials.map((s) => [s.serialNo, s.id]),
  );

  function handleSerialsChange(serials: string[]) {
    setSerialSelection(serials);
    // Resolve serial numbers to IDs for ones we know about.
    // Unknown ones (typed manually but not yet in the fetched list) are kept as-is
    // and the backend will reject them — which surfaces the error to the user.
    const ids = serials.map((sn) => serialNoToId[sn] ?? sn);
    onUpdateSerials(ids);
  }

  const assignedCount = (item.serials ?? []).length;
  const needsSerials = item.quantity > 0; // show serial button for all lines; backend decides if required

  return (
    <tbody>
      <tr className={["border-b hover:bg-muted/30 transition-colors", loading ? "opacity-50" : ""].join(" ")}>
        {/* SKU / name */}
        <td className="px-3 py-2">
          <div className="font-medium text-sm">{item.productName ?? "—"}</div>
          <div className="text-xs text-muted-foreground font-mono">{item.sku}</div>
        </td>

        {/* Qty */}
        <td className="px-3 py-2 w-24">
          <Input
            id={`qty-${item.id}`}
            type="number"
            min={1}
            value={qtyDraft}
            onChange={(e) => setQtyDraft(e.target.value)}
            onBlur={handleQtyBlur}
            onKeyDown={handleQtyKey}
            className="h-8 w-20 text-center text-sm"
          />
        </td>

        {/* Unit price — editable only with permission */}
        <td className="px-3 py-2 w-32">
          {canEditPrice ? (
            <Input
              type="number"
              min={0}
              value={priceDraft}
              onChange={(e) => setPriceDraft(e.target.value)}
              onBlur={handlePriceBlur}
              onKeyDown={handlePriceKey}
              className="h-8 w-28 text-right text-sm font-mono"
            />
          ) : (
            <div className="text-right text-sm font-mono">{fmtVND(item.unitPrice)}</div>
          )}
        </td>

        {/* Discount — editable only with permission */}
        <td className="px-3 py-2 w-32">
          {canEditPrice ? (
            <Input
              type="number"
              min={0}
              value={discountDraft}
              onChange={(e) => setDiscountDraft(e.target.value)}
              onBlur={handleDiscountBlur}
              onKeyDown={handleDiscountKey}
              className="h-8 w-28 text-right text-sm font-mono"
            />
          ) : (
            <div className="text-right text-sm font-mono">
              {parseFloat(item.discount) > 0 ? fmtVND(item.discount) : "—"}
            </div>
          )}
        </td>

        {/* Line total */}
        <td className="px-3 py-2 text-right font-semibold font-mono text-sm whitespace-nowrap">
          {fmtVND(item.lineTotal)} ₫
        </td>

        {/* Serial toggle */}
        <td className="px-3 py-2 w-32 text-center">
          {needsSerials && (
            <button
              type="button"
              onClick={() => setSerialOpen((o) => !o)}
              className={[
                "inline-flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors",
                assignedCount > 0
                  ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                  : "bg-muted text-muted-foreground hover:bg-accent",
              ].join(" ")}
            >
              <ScanLine size={12} />
              {assignedCount > 0 ? `${assignedCount} SN` : "Serial"}
              {serialOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            </button>
          )}
        </td>

        {/* Remove */}
        <td className="px-3 py-2 w-10">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
            onClick={onRemove}
            aria-label="Xóa dòng"
          >
            <Trash2 size={14} />
          </Button>
        </td>
      </tr>

      {/* Serial scan panel — inline below the row */}
      {serialOpen && needsSerials && (
        <tr className="border-b bg-muted/10">
          <td colSpan={7} className="px-4 py-3">
            <div className="max-w-xl">
              {!warehouseId && (
                <p className="text-xs text-amber-600 mb-2">
                  Chưa có thông tin kho — không thể lọc serial.
                </p>
              )}
              <SerialScanInput
                value={serialSelection}
                onChange={handleSerialsChange}
                expectedCount={item.quantity}
              />
              {availableSerials.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-muted-foreground mb-1">
                    Serial có sẵn trong kho ({availableSerials.length}):
                  </p>
                  <div className="flex flex-wrap gap-1 max-h-28 overflow-y-auto rounded border bg-background p-2">
                    {availableSerials.map((s) => {
                      const selected = serialSelection.includes(s.serialNo);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => {
                            if (selected) {
                              handleSerialsChange(serialSelection.filter((x) => x !== s.serialNo));
                            } else {
                              handleSerialsChange([...serialSelection, s.serialNo]);
                            }
                          }}
                          className={[
                            "rounded px-2 py-0.5 text-xs font-mono border transition-colors",
                            selected
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-muted hover:bg-accent text-foreground",
                          ].join(" ")}
                        >
                          {s.serialNo}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* TODO: Excel/CSV bulk serial import for selling — deferred to post-MVP */}
            </div>
          </td>
        </tr>
      )}
    </tbody>
  );
}

export function WholesaleLineTable({
  items,
  warehouseId,
  loading,
  canEditPrice,
  onUpdateQty,
  onUpdatePrice,
  onUpdateDiscount,
  onUpdateSerials,
  onRemove,
}: Props) {
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <div className="text-sm">Chưa có sản phẩm. Tìm kiếm để thêm dòng.</div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/50 text-xs text-muted-foreground uppercase tracking-wide">
            <th className="px-3 py-2 text-left">Sản phẩm</th>
            <th className="px-3 py-2 text-left">SL</th>
            <th className="px-3 py-2 text-right">Đơn giá</th>
            <th className="px-3 py-2 text-right">Chiết khấu</th>
            <th className="px-3 py-2 text-right">Thành tiền</th>
            <th className="px-3 py-2 text-center">Serial</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        {items.map((item) => (
          <LineRow
            key={item.id}
            item={item}
            warehouseId={warehouseId}
            loading={loading}
            canEditPrice={canEditPrice}
            onUpdateQty={(qty) => onUpdateQty(item.id, qty)}
            onUpdatePrice={(price) => onUpdatePrice(item.id, price)}
            onUpdateDiscount={(discount) => onUpdateDiscount(item.id, discount)}
            onUpdateSerials={(ids) => onUpdateSerials(item.id, ids)}
            onRemove={() => onRemove(item.id)}
          />
        ))}
      </table>
    </div>
  );
}
